"use client";

// CellAvatar — the ONE user profile mark, app-wide. The initial (or, when the
// person has set one, their photo) sits inside the innernet CELL silhouette
// (law_cell), never a bare circle: the cell clip-path carries the shape, the
// organism gradient fills it (and stays as the ground a photo fades in over),
// and the glow is a drop-shadow FILTER so it hugs the cell instead of a square
// box. A 2px RIM rides just inside the edge (law_cell_rim) so the face reads as
// seated rather than pasted — on the bloom and on a photograph alike. Used in
// the dashboard header, /me + /connectors headers, the admin shell, the share
// and company bylines, and the ours member rings.
//
// Every face in the product comes through here. A surface that needs a person's
// mark renders CellAvatar — it never re-draws the cell with its own clip.

import type { CSSProperties } from "react";
import { CELL, cellRimStroke } from "../lib/brand/cell";

export default function CellAvatar({
  name,
  src,
  size = 40,
  fontSize,
  className,
  style,
  shadow = true,
}: {
  name: string | null | undefined;
  /** uploaded avatar image url — when present the photo wears the cell. */
  src?: string | null;
  size?: number;
  /** override the initial's size (defaults to ~0.42× the tile). */
  fontSize?: string | number;
  className?: string;
  style?: CSSProperties;
  /** ⚠️ The glow is a `filter` on the CLIP-PATH'd element, so it renders INSIDE
   *  the cell and is cut square at its edge. Wherever the mark sits on a
   *  contrasting ground (and the cut is visible), pass `shadow={false}` and put
   *  the drop-shadow on an unclipped parent instead. */
  shadow?: boolean;
}) {
  const initial = (name?.trim()?.charAt(0) ?? "?").toUpperCase();
  // the clip is objectBoundingBox (0..1), so the ELEMENT's box must carry the
  // cell's native 1071×831 aspect — a square box stretches the silhouette into
  // the squarish orb it must never be. `size` is the mark's width; the height
  // follows the exact Figma proportion.
  const h = Math.round(size / CELL.aspect);
  return (
    <span
      className={`cell-avatar${className ? " " + className : ""}`}
      aria-hidden
      style={{
        position: "relative",
        width: size,
        height: h,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        clipPath: `url(#${CELL.clipId})`,
        fontSize: fontSize ?? Math.round(size * 0.42),
        lineHeight: 1.3,
        letterSpacing: "-0.03em",
        color: "var(--on-organism)",
        background: "linear-gradient(150deg, color-mix(in oklab, var(--organism-color) 88%, white), var(--organism-color))",
        ...(shadow
          ? { filter: "drop-shadow(0 3px 10px color-mix(in oklab, var(--organism-color) 45%, transparent))" }
          : null),
        ...style,
      }}
    >
      {initial}
      {src ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={src}
          alt=""
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      ) : null}
      {/* the rim, last so it lies over the photo as well as the bloom. Drawn as
          a centred stroke on the same path the clip is cut from — the clip eats
          the outer half, leaving exactly CELL_RIM_PX inside the edge. */}
      <svg
        viewBox={CELL.viewBox}
        preserveAspectRatio="none"
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      >
        <path
          d={CELL.path}
          fill="none"
          stroke="var(--cell-rim)"
          strokeWidth={cellRimStroke(size)}
        />
      </svg>
    </span>
  );
}
